import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, CheckCircle2, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";

export default function ReviewPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ name: "", company: "", text: "", rating: 5 });
  const [hover, setHover] = useState(0);
  const [state, setState] = useState("form");
  const [google, setGoogle] = useState("");

  useEffect(() => {
    document.title = "Rəy yazın — livemedia.az";
    api.get(`/review-invite/${token}`)
      .then((r) => { setInfo(r.data); setF((x) => ({ ...x, name: r.data.name })); if (r.data.done) { setGoogle(r.data.google_url || ""); setState("done"); } })
      .catch((e) => setErr(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (f.text.trim().length < 5) return setErr("Rəyinizi bir neçə sözlə yazın");
    setState("sending");
    setErr("");
    try {
      const { data } = await api.post(`/review-invite/${token}`, f);
      setGoogle(data.google_url || "");
      setState("done");
    } catch (e2) {
      setErr(errMsg(e2));
      setState("form");
    }
  };

  return (
    <section className="py-24 md:py-32" data-testid="review-page">
      <div className="container-lm"><div className="max-w-xl mx-auto">
        {!info && !err && <Loader2 className="animate-spin mx-auto" />}
        {err && !info && <p className="text-center text-[#B0453A]" data-testid="review-invalid">{err}</p>}
        {info && state === "done" && (
          <div className="text-center" data-testid="review-done">
            <CheckCircle2 size={52} className="mx-auto text-[#2F5D50]" strokeWidth={1.5} />
            <h1 className="font-display text-3xl font-semibold mt-5">Təşəkkür edirik!</h1>
            <p className="text-[#77736B] mt-3">Rəyiniz bizim üçün çox dəyərlidir.</p>
            {google && (
              <div className="mt-8 rounded-2xl bg-white border border-[#171715]/5 p-6" data-testid="review-google">
                <p className="font-semibold">Bizə daha bir böyük kömək edə bilərsiniz</p>
                <p className="text-sm text-[#77736B] mt-1">Eyni rəyi Google Xəritədə də paylaşın — bu, yeni müştərilərin bizi tapmasına kömək edir.</p>
                <button type="button" onClick={() => { navigator.clipboard?.writeText(f.text).catch(() => {}); window.open(google, "_blank", "noopener"); }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1a73e8] hover:bg-[#1666cf] text-white font-semibold py-3.5 transition-colors" data-testid="review-google-btn">
                  <Star size={16} fill="currentColor" /> Google-da rəy yaz
                </button>
                <p className="text-[11px] text-[#77736B] mt-2">Rəyinizin mətni kopyalandı — Google-da sadəcə yapışdırın.</p>
              </div>
            )}
            <Link to="/" className="btn-dark mt-8 inline-flex">Ana səhifəyə</Link>
          </div>
        )}
        {info && state !== "done" && (
          <form onSubmit={submit} className="bg-white rounded-2xl p-6 sm:p-9 border border-[#171715]/5" data-testid="review-form">
            <p className="eyebrow">Rəy</p>
            <h1 className="font-display text-3xl font-semibold mt-3">Bizimlə işləmək necə idi?</h1>
            <p className="text-sm text-[#77736B] mt-2">Bir dəqiqənizi ayırın — rəyiniz digər müştərilərə seçim etməyə kömək edəcək.</p>
            {info.google_url && (
              <a href={info.google_url} target="_blank" rel="noopener noreferrer" className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#1a73e8] hover:bg-[#1666cf] text-white font-semibold py-3.5 transition-colors" data-testid="review-google-direct-btn">
                <Star size={16} fill="currentColor" /> Birbaşa Google-da rəy yazın
              </a>
            )}
            {info.google_url && <p className="text-[11px] text-[#77736B] text-center mt-2">və ya aşağıda burada yazın</p>}
            <div className="flex gap-1.5 mt-6" onMouseLeave={() => setHover(0)} role="radiogroup" aria-label="Qiymətləndirmə">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setF({ ...f, rating: n })} onMouseEnter={() => setHover(n)} aria-label={`${n} ulduz`} data-testid={`review-star-${n}`}
                  className="transition-transform hover:scale-110">
                  <Star size={34} className="text-[#D6AD62]" fill={(hover || f.rating) >= n ? "#D6AD62" : "none"} strokeWidth={1.5} />
                </button>
              ))}
            </div>
            <label className="block text-xs text-[#77736B] mt-6">Adınız
              <input className="lm-input mt-1" maxLength={80} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="review-name" />
            </label>
            <label className="block text-xs text-[#77736B] mt-4">Şirkət / layihə (istəyə görə)
              <input className="lm-input mt-1" maxLength={120} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="review-company" />
            </label>
            <label className="block text-xs text-[#77736B] mt-4">Rəyiniz
              <textarea className="lm-input mt-1 min-h-[130px] resize-y" maxLength={1500} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} data-testid="review-text" />
            </label>
            {err && <p className="text-sm text-[#B0453A] mt-3" role="alert" data-testid="review-error">{err}</p>}
            <button className="btn-dark w-full mt-6" disabled={state === "sending"} data-testid="review-submit">
              {state === "sending" ? <Loader2 size={16} className="animate-spin" /> : "Göndər"}
            </button>
            <p className="text-[11px] text-[#77736B] mt-3">Rəy yoxlanıldıqdan sonra saytda dərc oluna bilər.</p>
          </form>
        )}
      </div></div>
    </section>
  );
}
